import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API, authHeaders } from "./adminApi";

const resolveUrl = (u) => (u.startsWith("/") ? `${API}${u}` : u);

const PhotoCard = ({ photo, onDelete, onCaption }) => {
  const [caption, setCaption] = useState(photo.caption || "");
  const [saving, setSaving] = useState(false);
  const dirty = caption !== (photo.caption || "");

  const save = async () => {
    setSaving(true);
    await onCaption(photo, caption);
    setSaving(false);
  };

  return (
    <div data-testid={`gallery-photo-${photo.id}`} className="rounded-lg border border-border/60 bg-white overflow-hidden">
      <img src={resolveUrl(photo.url)} alt={photo.caption || "Gallery"} className="w-full h-36 object-cover" loading="lazy" />
      <div className="p-2.5 space-y-2">
        <div className="flex gap-1.5">
          <Input
            data-testid={`gallery-caption-${photo.id}`}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption (optional)"
            className="font-body text-xs h-8"
          />
          {dirty && (
            <Button size="sm" variant="outline" onClick={save} disabled={saving} className="h-8 px-2 shrink-0" data-testid={`gallery-caption-save-${photo.id}`}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            </Button>
          )}
        </div>
        <button
          data-testid={`gallery-delete-${photo.id}`}
          onClick={() => onDelete(photo)}
          className="font-body text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
        >
          <Trash2 size={12} /> Remove
        </button>
      </div>
    </div>
  );
};

export const GalleryTab = () => {
  const [photos, setPhotos] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchPhotos = () => {
    fetch(`${API}/api/gallery`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setPhotos)
      .catch(() => setPhotos([]));
  };

  useEffect(fetchPhotos, []);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const valid = files.filter((f) => ["image/jpeg", "image/png", "image/webp", "image/gif"].includes(f.type) && f.size <= 8 * 1024 * 1024);
    if (valid.length < files.length) toast.error("Some files were skipped (images only, max 8MB each)");
    if (!valid.length) return;
    setUploading(true);
    let done = 0;
    for (const file of valid) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const up = await fetch(`${API}/api/admin/upload-image`, { method: "POST", headers: authHeaders(), body: fd });
        const upData = await up.json();
        if (!up.ok) throw new Error(upData.detail);
        const res = await fetch(`${API}/api/admin/gallery`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ url: upData.url, caption: "" }),
        });
        if (!res.ok) throw new Error();
        done += 1;
      } catch {
        toast.error(`Could not upload ${file.name}`);
      }
    }
    setUploading(false);
    if (done) {
      toast.success(`${done} photo${done > 1 ? "s" : ""} added to the gallery`);
      fetchPhotos();
    }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    const res = await fetch(`${API}/api/admin/gallery/${photo.id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) {
      toast.success("Photo removed");
      fetchPhotos();
    } else {
      toast.error("Could not remove photo");
    }
  };

  const saveCaption = async (photo, caption) => {
    const res = await fetch(`${API}/api/admin/gallery/${photo.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ url: photo.url, caption }),
    });
    if (res.ok) {
      toast.success("Caption saved");
      fetchPhotos();
    } else {
      toast.error("Could not save caption");
    }
  };

  if (photos === null) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div data-testid="admin-gallery-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="font-body text-sm text-muted-foreground">
          {photos.length} photos · shown on the Gallery page and homepage slider (newest first)
        </p>
        <Button data-testid="gallery-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-accent hover:bg-accent/90 text-white font-body rounded-full">
          {uploading ? <Loader2 size={15} className="animate-spin mr-1" /> : <Upload size={15} className="mr-1" />} Upload Photos
        </Button>
        <input ref={fileRef} data-testid="gallery-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={handleFiles} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((p) => (
          <PhotoCard key={p.id} photo={p} onDelete={deletePhoto} onCaption={saveCaption} />
        ))}
      </div>
    </div>
  );
};
